// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useOverflow.ts
 * @input Uses React useState, useCallback, useRef; useIsomorphicLayoutEffect
 * @output Exports useOverflow hook for measuring and managing horizontal overflow
 * @position Core hook; used by OverflowList and consumers for overflow patterns
 *
 * Measures children rendered in a hidden container to determine how many fit
 * in the available width, without flickering. Uses ResizeObserver to react
 * to container size changes.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/hooks/index.ts
 */
import { useState, useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect.js";
import { observeResize, unobserveResize } from "../utils/sharedResizeObserver.js";
/**
 * Hook for managing horizontal overflow of a list of items.
 *
 * Renders all items into a hidden measurement container, then calculates
 * how many fit in the visible container's width. Uses ResizeObserver to
 * recalculate when the container resizes.
 *
 * The measurement container should contain all items followed by the
 * overflow indicator element (if any). The indicator is identified by
 * a `data-overflow-indicator` attribute.
 *
 * @example
 * ```
 * const { containerRef, measureRef, visibleCount, hasOverflow } = useOverflow(5, {
 *   gap: 8,
 * });
 * ```
 */
export function useOverflow(itemCount, options = {}) {
  const {
    gap = 0,
    minVisibleItems = 0,
    collapseFrom = 'end',
    behavior = 'observeSelf'
  } = options;
  const observeParent = behavior === 'observeParent';
  const [visibleCount, setVisibleCount] = useState(itemCount);
  const containerElRef = useRef(null);
  const measureElRef = useRef(null);
  const observedElRef = useRef(null);
  const calculate = useCallback(() => {
    const container = containerElRef.current;
    const measure = measureElRef.current;
    if (!container || !measure) {
      return;
    }
    let availableWidth;
    if (observeParent && container.parentElement) {
      const parent = container.parentElement;
      const parentStyle = getComputedStyle(parent);
      availableWidth = parent.clientWidth - parseFloat(parentStyle.paddingLeft) - parseFloat(parentStyle.paddingRight);
    } else {
      availableWidth = container.offsetWidth;
    }
    const allChildren = Array.from(measure.children);

    // The measurement container holds itemCount items, plus optionally
    // an overflow indicator as the last child.
    const hasIndicator = allChildren.length > itemCount;
    const children = hasIndicator ? allChildren.slice(0, itemCount) : allChildren;
    const indicatorWidth = hasIndicator ? allChildren[allChildren.length - 1].offsetWidth : 0;
    if (children.length === 0) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- overflow count is derived from measured DOM widths
      setVisibleCount(0);
      return;
    }

    // Collect widths of all children
    const widths = children.map(child => child.offsetWidth);

    // Determine how many items fit
    let totalWidth = 0;
    let count = 0;
    const orderedWidths = collapseFrom === 'end' ? widths : [...widths].reverse();
    for (let i = 0; i < orderedWidths.length; i++) {
      const itemWidth = orderedWidths[i];
      const gapWidth = i > 0 ? gap : 0;
      const candidateWidth = totalWidth + itemWidth + gapWidth;

      // If this isn't the last item, we need to reserve space for the indicator
      const isLastItem = i === orderedWidths.length - 1;
      const reservedWidth = isLastItem ? 0 : indicatorWidth + (count > 0 || indicatorWidth > 0 ? gap : 0);
      if (candidateWidth + reservedWidth > availableWidth && count >= minVisibleItems) {
        break;
      }
      totalWidth = candidateWidth;
      count++;
    }

    // eslint-disable-next-line @eslint-react/set-state-in-effect -- overflow count is derived from measured DOM widths
    setVisibleCount(Math.max(Math.min(count, itemCount), minVisibleItems));
  }, [itemCount, gap, minVisibleItems, collapseFrom, observeParent]);
  const containerRef = useCallback(el => {
    containerElRef.current = el;

    // Clean up previous observation
    if (observedElRef.current) {
      unobserveResize(observedElRef.current);
      observedElRef.current = null;
    }
    if (el) {
      const target = observeParent && el.parentElement ? el.parentElement : el;
      observeResize(target, () => {
        calculate();
      });
      observedElRef.current = target;
    }
  }, [calculate, observeParent]);
  const measureRef = useCallback(el => {
    measureElRef.current = el;
    if (el) {
      calculate();
    }
  }, [calculate]);

  // Recalculate when itemCount changes
  useIsomorphicLayoutEffect(() => {
    calculate();
  }, [calculate]);
  const hasOverflow = visibleCount < itemCount;
  return {
    containerRef,
    measureRef,
    visibleCount,
    hasOverflow
  };
}